/**
 * Utility functions for practice pages
 */
import { findSubcourseByIdWithFallback } from './courseTreeUtils';

/**
 * Get practice parameters from location state or URL params
 * Used by QuickPractice, WritingPractice, and SpeakingPractice
 */
export const getPracticeParams = (location) => {
  // Try location state first
  if (location.state) {
    return {
      courseId: location.state.courseId,
      subcourseId: location.state.subcourseId,
      skillId: location.state.skillId,
      lessonId: location.state.lessonId,
    };
  }
  
  // Fallback to URL search params
  const searchParams = new URLSearchParams(location.search);
  return {
    courseId: searchParams.get('course-id'),
    subcourseId: searchParams.get('subcourse-id'),
    skillId: searchParams.get('skill-id'),
    lessonId: searchParams.get('lesson-id'),
  };
};

/**
 * Clear localStorage for practice session
 * Removes old answers and recordings to prevent stale data
 * @param {string} sessionId - Session ID
 * @param {string} practiceType - Practice type ('practice', 'speaking-practice', etc.)
 * @param {boolean} clearAudio - Whether to clear audio recordings (default: false, to keep audio for review page)
 */
export const clearPracticeStorage = (sessionId, practiceType = 'practice', clearAudio = false) => {
  if (!sessionId) return;
  
  const storageKey = `${practiceType}-answers-${sessionId}`;
  localStorage.removeItem(storageKey);
  
  // Clear all old practice answers from previous sessions
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith(`${practiceType}-answers-`)) {
      localStorage.removeItem(key);
    }
  });
  
  // changed by Cursor: audio recordings now live in Recoil state (cleared via resetAudioRecordings callback)
  // Only clear pronunciation assessments from localStorage (small data, OK for localStorage)
  if (practiceType === 'speaking-practice') {
    // Clear pronunciation assessments (not needed for review)
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('pronunciation-assessment-')) {
        localStorage.removeItem(key);
      }
    });
  }
};

/**
 * Extract request-id from submission result
 * Checks multiple possible locations in the response
 */
export const extractRequestId = (result) => {
  const receivedRequestId = 
    result.requestId || 
    result.data?.['request-id'] || 
    result.data?.requestId ||
    null;
  
  // Validate requestId format
  const isValidRequestId = receivedRequestId && 
    typeof receivedRequestId === 'string' && 
    receivedRequestId.trim() !== '';
  
  return {
    requestId: receivedRequestId,
    isValid: isValidRequestId,
    trimmed: isValidRequestId ? receivedRequestId.trim() : null,
  };
};

/**
 * Map internal courseType to user-facing practice label
 */
export const getPracticeTypeLabel = (courseType) => {
  const labels = {
    'Quick Practice': 'Quick Practice',
    'Course Practice': 'Quick Practice',
    'Writing Practice': 'Writing Practice',
    'Speaking Practice': 'Speaking Practice',
    'Skill Practice': 'Quick Practice',
    'Drill Vocab': 'Drill Vocab',
  };
  return labels[courseType] || courseType || 'Practice';
};

/**
 * Scope label: Subcourse Practice / Lesson Practice / Skill Practice
 */
export const getPracticeScopeLabel = (practiceParams = null, courseType = 'Course Practice') => {
  const skillId = practiceParams?.skillId || null;
  const lessonId = practiceParams?.lessonId || null;

  if (skillId || courseType === 'Skill Practice') {
    return 'Skill Practice';
  }
  if (lessonId) {
    return 'Lesson Practice';
  }
  return 'Subcourse Practice';
};

/**
 * Format backend subpractice-type for display
 */
export const formatSubpracticeTypeLabel = (rawType) => {
  if (!rawType) return null;

  const labels = {
    pronunciation: 'Pronunciation',
    speaking: 'Speaking',
    'quick-practice': 'Quick Practice',
    'writing-practice': 'Writing Practice',
    'speaking-practice': 'Speaking Practice',
  };

  const normalized = String(rawType).toLowerCase();
  return labels[normalized] || String(rawType)
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Resolve subpractice-type from session or question set
 */
export const deriveSubpracticeType = (sessionData = null, originalQuestions = []) => {
  const fromSession = sessionData?.['subpractice-type'] || sessionData?.subpracticeType;
  if (fromSession) return fromSession;

  if (!Array.isArray(originalQuestions) || originalQuestions.length === 0) {
    return null;
  }

  if (isAllPronunciation(originalQuestions)) {
    return 'pronunciation';
  }

  const uniqueTypes = [
    ...new Set(
      originalQuestions
        .map((question) => question['subpractice-type'] || question.subpracticeType)
        .filter(Boolean)
    ),
  ];

  return uniqueTypes.length === 1 ? uniqueTypes[0] : null;
};

/**
 * Resolve subcourse/lesson labels for result page banner
 */
export const getResultPracticeContext = ({
  sessionData = null,
  practiceParams = null,
  courseType = 'Course Practice',
  level = null,
  lessons = [],
  courseTree = null,
  courseTreeBaseline = null,
  originalQuestions = [],
}) => {
  const subcourseId = practiceParams?.subcourseId || sessionData?.['subcourse-id'] || null;
  const matchedSubcourse = findSubcourseByIdWithFallback(
    subcourseId,
    courseTree,
    courseTreeBaseline
  );

  const subcourseName =
    matchedSubcourse?.['subcourse-code'] ||
    matchedSubcourse?.subcourseCode ||
    matchedSubcourse?.code ||
    sessionData?.['subcourse-code'] ||
    sessionData?.['subcourse-name'] ||
    (level && level !== 'All Levels' ? level : null) ||
    null;

  const lessonId = practiceParams?.lessonId || null;
  let lessonName =
    sessionData?.['lesson-name'] ||
    sessionData?.['lesson-title'] ||
    null;

  if (!lessonName && lessonId && Array.isArray(lessons) && lessons.length > 0) {
    const matchedLesson = lessons.find(
      (lesson) => String(lesson.id || lesson._id) === String(lessonId)
    );
    lessonName = matchedLesson?.title || matchedLesson?.name || null;
  }

  const practiceTypeLabel = getPracticeTypeLabel(courseType);
  const subPracticeTypeLabel = getPracticeScopeLabel(practiceParams, courseType);

  const rawSubpracticeType = deriveSubpracticeType(sessionData, originalQuestions);
  const formattedSubpracticeType = formatSubpracticeTypeLabel(rawSubpracticeType);
  const subpracticeTypeLabel =
    formattedSubpracticeType &&
    formattedSubpracticeType !== practiceTypeLabel &&
    formattedSubpracticeType !== subPracticeTypeLabel
      ? formattedSubpracticeType
      : null;

  return {
    subcourseName,
    lessonName: lessonId ? lessonName : null,
    practiceTypeLabel,
    subPracticeTypeLabel,
    subpracticeTypeLabel,
  };
};

/**
 * Build result object for navigation to result page
 */
export const buildResultPageState = ({
  sessionData,
  courseType,
  practiceParams,
  apiResponse,
  originalQuestions,
  userAnswers,
  fromPage = '/learner/language-learning',
}) => {
  // Determine language based on fromPage or sessionData
  let language = sessionData?.['course-name'] || null;
  
  // If not found, infer from fromPage
  if (!language) {
    if (fromPage === '/learner/english-language-learning' || fromPage?.includes('english')) {
      language = 'English language learning';
    } else if (fromPage === '/learner/japanese-language-learning' || fromPage?.includes('japanese')) {
      language = 'Japanese language learning';
    } else {
      language = 'German language learning';
    }
  }
  
  return {
    language,
    courseType,
    level: sessionData?.['subcourse-name'] || 'All Levels',
    practiceParams,
    apiResponse,
    originalQuestions,
    userAnswers,
    sessionData,
    fromPage,
  };
};

/**
 * Retry mechanism with exponential backoff
 * @param {Function} submitFn - Function that returns a promise
 * @param {Object} options - Retry options
 * @param {number} options.baseDelay - Base delay in ms (default: 2000)
 * @param {number} options.maxDelay - Maximum delay cap in ms (default: 30000)
 * @param {number} options.maxRetries - Maximum number of retries (default: 10)
 * @param {Function} options.onRetry - Callback called on each retry
 * @returns {Promise} Promise that resolves with the result or rejects after max retries
 */
export const retryWithExponentialBackoff = async (
  submitFn,
  options = {}
) => {
  const {
    baseDelay = 2000,
    maxDelay = 30000,
    maxRetries = 10,
    onRetry,
  } = options;
  
  let attemptCount = 0;
  let lastError = null;
  
  while (attemptCount < maxRetries) {
    try {
      attemptCount++;
      const result = await submitFn();
      
      // If success, return immediately
      if (result && result.success && result.status !== 'error') {
        return { success: true, result, attemptCount };
      }
      
      // If max retries reached, throw error
      if (attemptCount >= maxRetries) {
        throw new Error(`Failed after ${maxRetries} attempts: ${result?.message || 'Unknown error'}`);
      }
      
      // Exponential backoff: delay increases exponentially with each retry
      const delay = Math.min(baseDelay * Math.pow(2, attemptCount - 1), maxDelay);
      if (onRetry) onRetry(attemptCount, delay);
      await new Promise(resolve => setTimeout(resolve, delay));
      
    } catch (error) {
      lastError = error;
      
      if (attemptCount >= maxRetries) {
        if (process.env.NODE_ENV === 'development') {
          console.error(`❌ Max retries reached (${maxRetries}):`, error);
        }
        throw error;
      }
      
      // Exponential backoff for errors too
      const delay = Math.min(baseDelay * Math.pow(2, attemptCount - 1), maxDelay);
      if (onRetry) onRetry(attemptCount, delay);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  // Should not reach here, but just in case
  throw lastError || new Error('Failed after maximum retries');
};

/**
 * Format user answers for speaking practice submission
 * Maps questions to answers with proper matching and fallbacks
 */
export const formatSpeakingUserAnswers = (questions, answers) => {
  return questions.map((question, index) => {
    // Try to find answer by ID first (most reliable)
    let existingAnswer = answers.find(a => 
      (a.questionId || a['soal-id']) === (question['soal-id'] || question.id)
    );
    
    // Fallback: if no match by ID, try to match by index
    if (!existingAnswer && answers[index]) {
      const answerAtIndex = answers[index];
      const answerQuestionId = answerAtIndex.questionId || answerAtIndex['soal-id'];
      const currentQuestionId = question['soal-id'] || question.id;
      
      if (answerQuestionId !== currentQuestionId && process.env.NODE_ENV === 'development') {
        console.warn(`⚠️ Question ${index + 1}: ID mismatch, using index fallback. Question ID: ${currentQuestionId}, Answer ID: ${answerQuestionId}`);
      }
      existingAnswer = answerAtIndex;
    }
    
    const answerDuration = existingAnswer?.duration || 0;
    const userAnswerText = existingAnswer?.userAnswer || existingAnswer?.answer || question.text || question['text-soal'] || question.question || question.prompt || '';
    
    return {
      'proset-id': existingAnswer?.prosetId || existingAnswer?.['proset-id'] || question['proset-id'],
      'soal-id': question['soal-id'] || question.id,
      type: 'essay',
      'user-answer': userAnswerText,
      duration: answerDuration,
    };
  });
};

/**
 * Format user answers for writing practice submission
 * Maps answers to questions with type and proset-id enrichment
 */
export const formatWritingUserAnswers = (questions, answers) => {
  return answers.map((answer, index) => {
    const soalId = answer.questionId || answer['soal-id'];
    
    // Find matching question from GET API by soal-id
    let originalQuestion = questions.find(q => q['soal-id'] === soalId);
    
    // Fallback: try matching by index if soal-id doesn't match
    if (!originalQuestion && questions[index]) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`⚠️ Question ${index + 1}: Matching by index (soal-id mismatch: ${soalId})`);
      }
      originalQuestion = questions[index];
    }
    
    if (!originalQuestion) {
      if (process.env.NODE_ENV === 'development') {
        console.error(`❌ Question ${index + 1}: Original question not found! soal-id: ${soalId}`);
      }
      return {
        'proset-id': answer.prosetId || answer['proset-id'],
        'soal-id': soalId,
        type: answer.type || 'qna',
        'user-answer': answer.answer || answer.userAnswer,
        duration: answer.duration || 0,
      };
    }
    
    return {
      'proset-id': answer.prosetId || answer['proset-id'] || originalQuestion['proset-id'],
      'soal-id': soalId,
      type: answer.type || originalQuestion.type,
      'user-answer': answer.answer || answer.userAnswer,
      duration: answer.duration || 0,
    };
  });
};

/**
 * Build submission data object for practice submission
 * Handles priority: lessonId > skillId > subcourseId > courseId
 */
export const buildSubmissionData = ({
  sessionData,
  practiceType,
  subpracticeType,
  practiceParams,
  userAnswers,
}) => {
  const lessonId = practiceParams.lessonId || sessionData['lesson-id'];
  const skillId = practiceParams.skillId || sessionData['skill-id'];
  const subcourseId = lessonId ? null : (practiceParams.subcourseId || sessionData['subcourse-id']);
  const courseId = (lessonId || subcourseId) ? null : (practiceParams.courseId || sessionData['course-id']);

  if (!courseId && !subcourseId && !skillId && !lessonId) {
    return { error: 'Missing required information. Please try again from the practice selection page.' };
  }

  const submissionData = {
    sessionId: sessionData['session-id'],
    practiceType,
    courseId,
    subcourseId,
    skillId,
    lessonId,
    userAnswers,
  };

  if (subpracticeType) {
    submissionData.subpracticeType = subpracticeType;
  }

  return { submissionData };
};

/**
 * Check if all questions are pronunciation type
 */
export const isAllPronunciation = (questions) => {
  return questions.every(q => {
    const questionType = q.type || q['question-type'] || '';
    return questionType.toLowerCase().includes('pronun') || 
           q['subpractice-type'] === 'pronunciation';
  });
};

